import { getAuthHeader, storeAuth } from './auth';
import { ROLE_LEVEL_MAP, ROLE_USERGROUP_MAP } from '../features/staff/api/constants';
import { findLevel5ParentOrgUnit } from './orgUnitUtils';

const BASE_URL = process.env.REACT_APP_DHIS2_BASE_URL || '';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
    ? '/backend-api' 
    : 'http://localhost:3001');


// For self-registration (no auth required)
/*
export const registerUser = async (userData) => {
  const MAX_RETRIES = 5;
  const RETRY_DELAY = 2000; // 2 seconds
  let userId = null;
  let accountCreated = false;

  try {
    // 1. Create user account
    const formData = new FormData();
    formData.append('firstName', userData.firstName);
    formData.append('surname', userData.lastName);
    formData.append('username', userData.username);
    formData.append('password', userData.password);
    formData.append('retypePassword', userData.password);
    formData.append('email', userData.email);
    formData.append('phoneNumber', userData.phoneNumber || '');


    console.log('Creating account for:', userData.username);
    console.log('🚀 API Base URL:', BASE_URL);
    console.log('🚀 Full API Endpoint:', `${BASE_URL}/api/account`);


    const accountResponse = await fetch(`${BASE_URL}/api/account`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!accountResponse.ok) {
      const error = await accountResponse.json();
      throw new Error(error.message || 'Account creation failed');
    }

    accountCreated = true;
    storeAuth(userData.username, userData.password);
    console.log('Account created, waiting for user propagation...');

    // 2. Wait for user to appear in main user store
    let retries = 0;
    while (retries < MAX_RETRIES) {
      try {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));

        console.log(`Checking user existence (attempt ${retries + 1})`);
        const userCheck = await fetch(
          `${BASE_URL}/api/users?filter=username:eq:${userData.username}&fields=id`,
          { headers: getAuthHeader() }
        );

        if (userCheck.ok) {
          const data = await userCheck.json();
          if (data.users && data.users.length > 0) {
            userId = data.users[0].id;
            console.log('User found in main store with ID:', userId);
            break;
          }
        }
      } catch (error) {
        console.warn('User check failed:', error.message);
      }
      retries++;
    }

    if (!userId) {
      throw new Error('User not found in main store after creation');
    }

    // 3. Assign role with retries
    retries = 0;
    let roleAssigned = false;

    while (retries < MAX_RETRIES && !roleAssigned) {
      try {
        console.log(`Assigning role (attempt ${retries + 1})`);
        const roleResponse = await fetch(
          `${BASE_URL}/api/users/${userId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeader()
            },
            body: JSON.stringify({
              userCredentials: {
                userRoles: [{ id: 'R3lFolCMdoi' }]
              }
            })
          }
        );

        if (roleResponse.ok || roleResponse.status === 500) {
          roleAssigned = true;
          console.log('Role assignment successful (status:', roleResponse.status, ')');
        } else {
          const errorData = await roleResponse.text();
          throw new Error(errorData.message || 'Role assignment failed');
        }
      } catch (error) {
        console.warn('Role assignment failed:', error.message);
        if (retries < MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
        retries++;
      }
    }

    if (!roleAssigned) {
      throw new Error(`Failed to assign role after ${MAX_RETRIES} attempts`);
    }

    // **** NO org unit clearing here! ****

    console.log('Registration process completed');
    return {
      success: true,
      username: userData.username,
      userId,
      message: '✅ Registration successful'
    };

  } catch (error) {
    console.error('❌Registration error:', {
      username: userData.username,
      userId,
      error: error.message,
      accountCreated,
      timestamp: new Date().toISOString()
    });

    // Transform error for UI
    let userError = error.message;
    if (error.message.includes('not found in main store')) {
      userError = 'Account is being processed. Please try logging in later.';
    } else if (error.message.includes('Failed to assign role')) {
      userError = 'Account created but needs role assignment. Support has been notified.';
    }

    throw new Error(userError);
  }
};
*/

export const registerUser = async (userData) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            firstName: userData.firstName,
            lastName: userData.lastName,
            username: userData.username,
            email: userData.email,
            password: userData.password,
            phoneNumber: userData.phoneNumber || '',
            gender: userData.gender || ''
        })
    });

    // If response is not OK, try to parse error message
    if (!response.ok) {
        let errorMessage = 'Registration failed';
        try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
        } catch (_) {
            // If response is not JSON (e.g., HTML), use status text
            errorMessage = `Server error (${response.status})`;
        }
        throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Registration failed');
    }
    return data; // { success: true, message: '...' }
};


// For admin operations (will use the auth from DataProvider context)
export const adminApiRequest = async (endpoint, method = 'GET', body = null) => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeader()
  };

  const response = await fetch(`${BASE_URL}/api/${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Admin request failed');
  }

  return response.json();
};

// Example admin-specific operations
export const elevateUserRole = async (userId, roles, auth) => {
  try {
    const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth) // Pass admin credentials
      },
      body: JSON.stringify({
        userCredentials: {
          userRole: roles
        }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Role elevation failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Role elevation failed:', {
      error,
      userId,
      roles,
      suggestion: 'Verify user has proper permissions'
    });
    throw error;
  }
};

export const verifyUsername = async (username, isPublic = false) => {
  // Client-side validation
  if (!username || username.length < 4) {
    return false;
  }

  try {
    const url = `${BASE_URL}/api/users?filter=username:eq:${username}&fields=id`;
    const options = {
      credentials: 'include'
    };

    // Only include auth headers if not in public mode
    if (!isPublic) {
      options.headers = getAuthHeader();
    }

    const response = await fetch(url, options);

    if (response.status === 401 && isPublic) {
      // Special handling for public access attempt
      console.warn('Username verification not available publicly');
      return true; // Allow registration to proceed
    }

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const { users } = await response.json();
    return users.length === 0;
  } catch (error) {
    console.error('Username check failed:', error);
    return true; // Don't block on error
  }
};


export async function assignUserToOrgs(userId, orgUnits, authOverride) {
  try {
    // If no org units provided, do nothing
    if (!orgUnits || orgUnits.length === 0) {
      console.warn('No org units provided for assignment');
      return;
    }

    // Get the main org unit (first one in the array)
    const mainOrgUnit = orgUnits[0];

    // Find the Level 5 parent for the main org unit
    const level5OrgUnit = await findLevel5ParentOrgUnit(mainOrgUnit.id, authOverride);

    // Create array with only the Level 5 org unit
    const level5OrgUnits = [level5OrgUnit];

    // Fetch current user data
    const userRes = await fetch(
      `${BASE_URL}/api/users/${userId}?fields=:all`,
      { headers: getAuthHeader(authOverride) }
    );

    if (!userRes.ok) {
      const errText = await userRes.text();
      throw new Error(`Failed to fetch user before assignment: ${errText}`);
    }

    const userObj = await userRes.json();

    // Update user with only Level 5 org units
    userObj.organisationUnits = level5OrgUnits.map(ou => ({ id: ou.id, name: ou.name }));
    userObj.dataViewOrganisationUnits = level5OrgUnits.map(ou => ({ id: ou.id }));

    // Send update to server
    const assignRes = await fetch(
      `${BASE_URL}/api/users/${userId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader(authOverride) },
        body: JSON.stringify(userObj)
      }
    );

    if (!assignRes.ok) {
      const err = await assignRes.text();
      throw new Error('Failed to assign user to org units: ' + err);
    }

    console.log(`Successfully assigned user ${userId} to Level 5 org unit: ${level5OrgUnit.name}`);

  } catch (error) {
    console.error('Error in assignUserToOrgs:', error);
    throw error;
  }
}


export async function assignUserToSpecificOrgs(userId, orgUnits, authOverride) {
  try {
    const userRes = await fetch(
      `${BASE_URL}/api/users/${userId}?fields=:all`,
      { headers: getAuthHeader(authOverride) }
    );

    if (!userRes.ok) {
      const errText = await userRes.text();
      throw new Error(`Failed to fetch user before assignment: ${errText}`);
    }

    const userObj = await userRes.json();
    userObj.organisationUnits = orgUnits.map(ou => ({ id: ou.id, name: ou.name }));
    userObj.dataViewOrganisationUnits = orgUnits.map(ou => ({ id: ou.id }));

    const assignRes = await fetch(
      `${BASE_URL}/api/users/${userId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader(authOverride) },
        body: JSON.stringify(userObj)
      }
    );

    if (!assignRes.ok) {
      const err = await assignRes.text();
      throw new Error('Failed to assign user to org units: ' + err);
    }

    console.log(`Assigned user ${userId} to ${orgUnits.length} specific org units`);

  } catch (error) {
    console.error('Error in assignUserToSpecificOrgs:', error);
    throw error;
  }
}


export function buildTrackedEntityPayload(formData, trackedEntityTypeId, username, userId) {
  // Validate required fields
  if (!username) throw new Error('Username is required for TEI creation');
  if (!trackedEntityTypeId) throw new Error('Tracked Entity Type ID is required');
  if (!formData.orgUnit) throw new Error('Organization Unit is required');
  if (!userId) throw new Error('User ID is required for linkage');

  return {
    trackedEntityType: trackedEntityTypeId, // Use the parameter
    orgUnit: formData.orgUnit,
    attributes: [
      // Personal info
      { attribute: "fqqM9M2HOTS", value: formData.firstName },
      { attribute: "dlG2zF6SdRy", value: formData.secondName },
      { attribute: "HAOvVNYm8Lx", value: formData.gender },

      // Organization info
      { attribute: "HZG6mhL4FL5", value: formData.department || "cA5anOEWYBo", skipValidation: true },
      { attribute: "Wgs4UnYF8Vz", value: formData.userRole },

      // Contact info
      { attribute: "aOfR1b3W2gv", value: formData.phoneNumber },
      { attribute: "zjwngtjgvLB", value: formData.email },
      { attribute: "Ndsp9fQExRc", value: formData.dob },

      // Linkage attributes
      { attribute: "g3RRIfDiQra", value: username },
      { attribute: "n8aAqQTVI8b", value: userId }
    ].filter(attr => {
      // Filter out empty values but keep the linkage attributes
      if (attr.attribute === "g3RRIfDiQra" || attr.attribute === "n8aAqQTVI8b") return true;
      return attr.value !== undefined && attr.value !== null && attr.value !== '';
    })
  };
}


export async function createLinkedTEI(teiData, auth) {
    console.log('🔍 [DEBUG] createLinkedTEI received data:', {
        username: teiData.username,
        hasDepartmentField: teiData.department !== undefined,
        departmentValue: teiData.department,
        hasDepartmentName: teiData.departmentName !== undefined,
        departmentNameValue: teiData.departmentName,
        allFields: Object.keys(teiData)
    });

    // ✅ Check if we have a valid userId
    if (!teiData.userId) {
        console.warn('⚠️ No userId provided, TEI creation will be skipped');
        return {
            response: { importSummaries: [{ reference: null, status: 'SKIPPED' }] },
            skipped: true,
            reason: 'No userId provided'
        };
    }

    // ✅ Check if we have a valid orgUnit
    if (!teiData.orgUnit) {
        console.warn('⚠️ No orgUnit provided, TEI creation will be skipped');
        return {
            response: { importSummaries: [{ reference: null, status: 'SKIPPED' }] },
            skipped: true,
            reason: 'No orgUnit provided'
        };
    }

    let departmentName = teiData.department || "";
    if (!departmentName && teiData.departmentName) {
        departmentName = teiData.departmentName;
    }
    if (!departmentName) {
        departmentName = "Unknown Department";
    }

    console.log('📍 Department for TEI:', departmentName);

    let existingTeiId = null;

    // Include BOTH trackedEntityType AND ou (required by DHIS2 API)
    try {
        const staffCheckUrl = `${BASE_URL}/api/trackedEntityInstances.json?trackedEntityType=OaAbpIs1cV7&ou=${teiData.orgUnit}&filter=g3RRIfDiQra:eq:${teiData.username}&fields=trackedEntityInstance,orgUnit&paging=false`;
        console.log('🔍 Checking for existing TEI at:', staffCheckUrl);
        
        const staffResponse = await fetch(staffCheckUrl, {
            method: 'GET',
            credentials: 'include',
            headers: getAuthHeader(auth)
        });

        if (staffResponse.ok) {
            const staffData = await staffResponse.json();
            if (staffData.trackedEntityInstances && staffData.trackedEntityInstances.length > 0) {
                existingTeiId = staffData.trackedEntityInstances[0].trackedEntityInstance;
                console.log('✅ Found existing STAFF TEI:', existingTeiId);
                return {
                    response: {
                        importSummaries: [{
                            reference: existingTeiId,
                            status: 'SUCCESS'
                        }]
                    },
                    existingTei: true,
                    teiType: 'STAFF'
                };
            } else {
                console.log('ℹ️ No existing TEI found for username:', teiData.username);
            }
        } else {
            console.warn('⚠️ TEI check failed with status:', staffResponse.status);
        }
    } catch (checkError) {
        console.warn('⚠️ Staff TEI existence check failed:', checkError.message);
    }

    // ✅ Create TEI WITHOUT enrollment
    const simplePayload = {
        trackedEntityInstance: {
            trackedEntityType: "OaAbpIs1cV7",
            orgUnit: teiData.orgUnit,
            attributes: [
                { attribute: "g3RRIfDiQra", value: teiData.username },
                ...(teiData.userId ? [{ attribute: "n8aAqQTVI8b", value: teiData.userId }] : []),
                { attribute: "fqqM9M2HOTS", value: teiData.firstName },
                { attribute: "dlG2zF6SdRy", value: teiData.secondName || teiData.surname || '' },
                { attribute: "HAOvVNYm8Lx", value: teiData.gender || 'Unknown' },
                { attribute: "HZG6mhL4FL5", value: departmentName },
                ...(teiData.email ? [{ attribute: "zjwngtjgvLB", value: teiData.email }] : []),
                ...(teiData.phoneNumber ? [{ attribute: "aOfR1b3W2gv", value: teiData.phoneNumber }] : []),
            ].filter(attr => attr.value && attr.value.trim() !== '')
        }
    };

    console.log('📦 TEI Payload (without enrollment):', JSON.stringify(simplePayload, null, 2));

    try {
        const res = await fetch(`${BASE_URL}/api/trackedEntityInstances`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader(auth)
            },
            body: JSON.stringify(simplePayload)
        });

        if (!res.ok) {
            const errorText = await res.text();
            console.error('❌ TEI Creation HTTP Error:', errorText);
            
            console.warn('⚠️ TEI creation failed, continuing without TEI');
            return {
                response: { importSummaries: [{ reference: null, status: 'SKIPPED' }] },
                skipped: true,
                error: errorText
            };
        }

        const response = await res.json();
        console.log('✅ TEI created successfully:', response);
        
        // Extract the TEI ID from the response
        const teiId = response?.response?.importSummaries?.[0]?.reference || 
                     response?.importSummaries?.[0]?.reference ||
                     response?.uid;
        
        if (teiId) {
            console.log('✅ TEI ID:', teiId);
        }

        return {
            ...response,
            teiType: 'STAFF',
            teiId: teiId
        };

    } catch (error) {
        console.error('❌ TEI Creation Failed:', error.message);
        return {
            response: { importSummaries: [{ reference: null, status: 'SKIPPED' }] },
            skipped: true,
            error: error.message
        };
    }
}

export async function getOrgUnitsByLevel(engine, level, rootOrgUnitId) {

  // Extract the actual ID from the  response object
  let actualOrgUnitId;

  if (typeof rootOrgUnitId === 'object' && rootOrgUnitId !== null) {
    // Try common response structures

    if (rootOrgUnitId.id) {
      // Direct ID property
      actualOrgUnitId = rootOrgUnitId.id;
    } else if (rootOrgUnitId.response?.id) {
      // Nested in response object
      actualOrgUnitId = rootOrgUnitId.response.id;
    } else if (rootOrgUnitId.organisationUnits?.[0]?.id) {
      // From organisationUnits array
      actualOrgUnitId = rootOrgUnitId.organisationUnits[0].id;
    } else if (rootOrgUnitId.organisationUnit?.id) {
      // From organisationUnit object
      actualOrgUnitId = rootOrgUnitId.organisationUnit.id;
    } else {
      console.error('Could not extract ID from object:', rootOrgUnitId);
      throw new Error('Could not extract organisation unit ID from response object');
    }

    console.log('Extracted ID from object:', actualOrgUnitId);
  } else if (typeof rootOrgUnitId === 'string') {
    // Already a string ID
    actualOrgUnitId = rootOrgUnitId;
    console.log('Using string ID:', actualOrgUnitId);
  } else {
    throw new Error(`Invalid organisation unit ID: ${typeof rootOrgUnitId} - ${rootOrgUnitId}`);
  }

  if (!actualOrgUnitId) {
    throw new Error('No organisation unit ID provided');
  }


  try {
    // Use the engine approach - pass engine as second parameter
    const orgUnitTree = await fetchOrgUnitTree(engine, actualOrgUnitId);

    // Flatten the tree and filter by level
    const flattenOrgUnits = (orgUnit) => {
      let list = [{ id: orgUnit.id, name: orgUnit.name, level: orgUnit.level }];
      if (orgUnit.children) {
        orgUnit.children.forEach(child => {
          list = list.concat(flattenOrgUnits(child));
        });
      }
      return list;
    };

    const allDescendants = flattenOrgUnits(orgUnitTree);
    const unitsAtLevel = allDescendants.filter(ou => ou.level === level);

    return unitsAtLevel;
  } catch (error) {
    console.error('Error in getOrgUnitsByLevel:', error);
    throw error;
  }
}

async function createEnrollment({ teiId, programId, orgUnitId, enrollmentDate, auth }) {
  const url = `${BASE_URL}/api/enrollments`;

  const payload = {
    trackedEntityInstance: teiId,
    program: programId,
    orgUnit: orgUnitId,
    enrollmentDate: enrollmentDate,
    incidentDate: enrollmentDate,
    status: 'ACTIVE'
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(auth),
    },
    body: JSON.stringify(payload),
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.message || `Enrollment creation failed with status ${response.status}`);
  }

  return await response.json();
}


// Main creation function - preserves your original logic
export async function createStaffWithTEI(engine, staffData) {
  if (!staffData) {
    throw new Error('staffData is required');
  }

  console.log('🔍 Staff data received in createStaffWithTEI:', staffData);
  console.log('🔍 User role value:', staffData.userRole);

  let userResponse = null;
  let userRootOrgUnitId = null;

  try {
    // Step 1: Check existing user
    const existingUser = await checkUserExistsByUsername(staffData.username, engine);

    if (existingUser) {
      throw new Error(`User with username '${staffData.username}' already exists`);
    }

    // Step 2: Validate userRole exists
    const userRole = staffData.userRole;
    if (!userRole) {
      throw new Error('User role is required for staff creation. Please select a role.');
    }

    const userGroupId = ROLE_USERGROUP_MAP[userRole];
    if (!userGroupId) {
      throw new Error(`No user group mapping found for role: ${userRole}`);
    }

    const targetLevel = ROLE_LEVEL_MAP[userRole];
    if (!targetLevel) {
      throw new Error(`No organisation unit level mapping found for role: ${userRole}`);
    }

    console.log(`Target level for role ${userRole}: ${targetLevel}`);

    // Get user's organisation units
    const { me } = await engine.query({
      me: {
        resource: 'me',
        params: { fields: 'organisationUnits[id,name,level]' }
      }
    });

    userRootOrgUnitId = me.organisationUnits?.[0]?.id;
    console.log('User root org unit ID:', userRootOrgUnitId);

    if (!userRootOrgUnitId) {
      throw new Error('No organisation unit found for current user');
    }

    // Get organisation units at the target level
    const orgUnitsAtLevel = await getOrgUnitsByLevel(engine, targetLevel, userRootOrgUnitId);

    if (orgUnitsAtLevel.length === 0) {
      throw new Error(`No organisation units found at level ${targetLevel} for role ${userRole}`);
    }

    // For simplicity, assign to the first org unit at that level
    const targetOrgUnit = orgUnitsAtLevel[0];
    console.log(`✅ Assigning to org unit: ${targetOrgUnit.name} (Level ${targetLevel})`);

    // ✅ NEW: Get parent org unit for search visibility
    let dataViewOrgUnits = [{ id: targetOrgUnit.id }];
    console.log('🔍 Getting parent org unit for search visibility...');

    const parentOrgUnit = await getParentOrgUnit(engine, targetOrgUnit.id);
    if (parentOrgUnit && parentOrgUnit.id) {
      console.log(`✅ Adding parent org unit for search: ${parentOrgUnit.name} (${parentOrgUnit.id})`);
      dataViewOrgUnits.push({ id: parentOrgUnit.id });
    } else {
      console.log('⚠️ No parent org unit found, user will only search in their own unit');
    }

    // Step 3: Create user with automatically assigned org unit
    userResponse = await createDHIS2User({
      firstName: staffData.firstName,
      surname: staffData.secondName,
      email: staffData.email,
      phoneNumber: staffData.phoneNumber,
      organisationUnits: [{ id: targetOrgUnit.id }],
      userRole: userRole,
      attributeValues: staffData.dob ? [{ attribute: 'Ndsp9fQExRc', value: staffData.dob }] : [],
      userCredentials: {
        username: staffData.username,
        password: staffData.password || 'Default@123',
        userRoles: [{ id: userRole }]
      }
    }, engine);

    if (!userResponse?.response?.uid) {
      throw new Error('User creation failed: No user ID returned');
    }

    const userId = userResponse.response.uid;
    console.log(`✅ User created successfully with ID: ${userId}`);

    // ✅ CRITICAL: UPDATE USER TO SET SEARCH VISIBILITY
    console.log('🔧 Setting search visibility for user...');
    await forceSearchOrgUnitsUpdate(engine, userId, dataViewOrgUnits);

    console.log('📍 Assigned org units:', [{ id: targetOrgUnit.id }]);
    console.log('🔍 Searchable org units:', dataViewOrgUnits);

    let userGroupResult = null;
    try {
      console.log(`🔗 Starting user group assignment for: ${userGroupId}`);
      userGroupResult = await addUserToUserGroup(userId, userGroupId, engine);
      console.log(`✅ User group assignment completed:`, userGroupResult);
    } catch (userGroupError) {
      console.warn('⚠️ User group assignment failed, but continuing:', userGroupError.message);
      userGroupResult = {
        success: false,
        error: userGroupError.message,
        requiresManualAssignment: true
      };
    }

    // Wait for user to be fully persisted
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Step 4: Create TEI with the same org unit
    const teiResponse = await createLinkedTEI({
      ...staffData,
      userId: userId,
      orgUnit: targetOrgUnit.id,
      userRole: userRole,
      department: staffData.department || "",
      departmentName: staffData.department || ""
    }, engine);

    if (!teiResponse?.response?.importSummaries?.[0]?.reference) {
      throw new Error('TEI creation failed or no reference returned');
    }

    const teiId = teiResponse.response.importSummaries[0].reference;
    console.log(`✅ TEI created successfully with ID: ${teiId}`);

    const enrollmentId = teiResponse.response.importSummaries[0].enrollments?.importSummaries?.[0]?.reference;
    console.log(`✅ Enrollment created with TEI, ID: ${enrollmentId || 'N/A'}`);

    return {
      user: userResponse,
      tei: teiResponse,
      enrollment: teiResponse,
      userId,
      teiId,
      enrollmentId,
      userGroupAssignment: userGroupResult
    };

  } catch (error) {
    if (userResponse?.response?.uid) {
      console.log('🔄 Cleaning up: Deleting created user due to failure');
      await deleteUser(userResponse.response.uid, engine).catch(cleanupError =>
        console.error('Cleanup failed:', cleanupError)
      );
    }

    console.error('❌ Complete error in createStaffWithTEI:', error);
    throw new Error(`Staff creation failed: ${error.message}`);
  }
}


// src/utils/api.js - Complete fixed createTEIOnly

export async function createTEIOnly(engine, staffData, checkExistingFirst = true) {
    if (!staffData) {
        throw new Error('staffData is required');
    }

    console.log('🔍 [CREATE TEI ONLY] Starting for:', staffData.username);
    console.log('🔍 Staff data received:', staffData);

    try {
        // Step 1: Validate userRole exists and get role details
        const userRole = staffData.userRole;
        if (!userRole) {
            throw new Error('User role is required for TEI creation');
        }

        const targetLevel = ROLE_LEVEL_MAP[userRole];
        if (!targetLevel) {
            throw new Error(`No organisation unit level mapping found for role: ${userRole}`);
        }

        console.log(`Target level for role ${userRole}: ${targetLevel}`);

        // Step 2: Get organisation unit
        let targetOrgUnit;

        if (staffData.orgUnit) {
            // Use the provided orgUnit
            targetOrgUnit = { id: staffData.orgUnit };
            console.log(`✅ Using provided org unit: ${staffData.orgUnit}`);
        } else {
            // Determine org unit from level
            const { me } = await engine.query({
                me: {
                    resource: 'me',
                    params: { fields: 'organisationUnits[id,name,level]' }
                }
            });

            const userRootOrgUnitId = me.organisationUnits?.[0]?.id;
            console.log('User root org unit ID:', userRootOrgUnitId);

            if (!userRootOrgUnitId) {
                throw new Error('No organisation unit found for current user');
            }

            const orgUnitsAtLevel = await getOrgUnitsByLevel(engine, targetLevel, userRootOrgUnitId);

            if (orgUnitsAtLevel.length === 0) {
                throw new Error(`No organisation units found at level ${targetLevel} for role ${userRole}`);
            }

            targetOrgUnit = orgUnitsAtLevel[0];
            console.log(`✅ Auto-assigned to org unit: ${targetOrgUnit.name} (Level ${targetLevel})`);
        }

        // Get the org unit ID properly
        const orgUnitId = typeof targetOrgUnit === 'object' ? targetOrgUnit.id : targetOrgUnit;

        // Step 3: Check if TEI already exists BEFORE creating
        if (checkExistingFirst) {
            try {
                console.log('🔍 Checking if TEI already exists for username:', staffData.username);

                // ✅ FIX: Use the correct URL format with both trackedEntityType and ou
                const checkUrl = `${BASE_URL}/api/trackedEntityInstances.json?trackedEntityType=OaAbpIs1cV7&ou=${orgUnitId}&filter=g3RRIfDiQra:eq:${staffData.username}&fields=trackedEntityInstance,orgUnit,attributes[attribute,value]&paging=false`;
                
                console.log('🔍 Checking URL:', checkUrl);

                const checkResponse = await fetch(checkUrl, {
                    method: 'GET',
                    credentials: 'include',
                    headers: getAuthHeader()
                });

                if (checkResponse.ok) {
                    const checkData = await checkResponse.json();
                    const existingTeis = checkData.trackedEntityInstances || [];

                    if (existingTeis.length > 0) {
                        console.log('✅ Found existing TEI(s):', existingTeis.length);

                        // Check if any existing TEI is in the correct org unit
                        const teiInCorrectOrgUnit = existingTeis.find(tei =>
                            tei.orgUnit === orgUnitId
                        );

                        if (teiInCorrectOrgUnit) {
                            console.log('✅ Using existing TEI in correct org unit:', teiInCorrectOrgUnit.trackedEntityInstance);

                            // Extract attributes from the existing TEI
                            const existingAttributes = {};
                            if (teiInCorrectOrgUnit.attributes) {
                                teiInCorrectOrgUnit.attributes.forEach(attr => {
                                    existingAttributes[attr.attribute] = attr.value;
                                });
                            }

                            console.log('📋 Existing TEI attributes:', existingAttributes);

                            return {
                                tei: { existingTei: true },
                                teiId: teiInCorrectOrgUnit.trackedEntityInstance,
                                enrollmentId: null,
                                orgUnit: orgUnitId,
                                userRole: userRole,
                                existingTei: true,
                                reason: 'TEI already exists in correct org unit'
                            };
                        } else {
                            console.log('⚠️ Existing TEI found but in wrong org unit. Will create new one in correct org unit.');
                            // Continue with TEI creation in the correct org unit
                        }
                    } else {
                        console.log('✅ No existing TEI found, will create new one');
                    }
                } else {
                    console.warn('⚠️ TEI check failed with status:', checkResponse.status);
                    const errorText = await checkResponse.text();
                    console.warn('⚠️ Error details:', errorText);
                }
            } catch (error) {
                console.warn('⚠️ Error checking for existing TEI:', error.message);
                // Continue with creation if check fails
            }
        }

        // Step 4: Create TEI with retry logic (only if no existing TEI found or check was skipped)
        console.log('🔄 Proceeding with TEI creation...');
        let retries = 3;
        let lastError;

        while (retries > 0) {
            try {
                console.log(`🔄 TEI creation attempt ${4 - retries}/3`);

                const teiResponse = await createLinkedTEI({
                    ...staffData,
                    userId: staffData.userId || "",
                    orgUnit: orgUnitId,
                    userRole: userRole
                }, engine);

                if (teiResponse.existingTei) {
                    console.log('ℹ️ Using existing TEI record (from createLinkedTEI)');
                }

                // ✅ Handle skipped TEI creation
                if (teiResponse.skipped) {
                    console.warn('⚠️ TEI creation was skipped:', teiResponse.reason || teiResponse.error);
                    return {
                        tei: teiResponse,
                        teiId: null,
                        enrollmentId: null,
                        orgUnit: orgUnitId,
                        userRole: userRole,
                        existingTei: false,
                        skipped: true,
                        reason: teiResponse.reason || 'TEI creation skipped'
                    };
                }

                const teiId = teiResponse.response?.importSummaries?.[0]?.reference;
                if (!teiId) {
                    throw new Error('TEI creation failed or no reference returned');
                }

                console.log(`✅ TEI created/retrieved successfully with ID: ${teiId}`);

                const enrollmentId = teiResponse.response?.importSummaries?.[0]?.enrollments?.importSummaries?.[0]?.reference;
                console.log(`✅ Enrollment created with TEI, ID: ${enrollmentId || 'N/A'}`);

                return {
                    tei: teiResponse,
                    teiId,
                    enrollmentId,
                    orgUnit: orgUnitId,
                    userRole: userRole,
                    existingTei: teiResponse.existingTei || false
                };

            } catch (error) {
                lastError = error;
                console.warn(`⚠️ TEI creation attempt failed:`, error.message);
                retries--;

                if (retries > 0) {
                    console.log(`⏳ Retrying in 2 seconds... (${retries} attempts remaining)`);
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }
        }

        // Step 5: If all retries failed, check if TEI might already exist as a last resort
        console.log('🔍 All retries failed, doing final check if TEI might already exist...');
        try {
            // ✅ FIX: Use the correct URL format with both trackedEntityType and ou
            const finalCheckUrl = `${BASE_URL}/api/trackedEntityInstances.json?trackedEntityType=OaAbpIs1cV7&ou=${orgUnitId}&filter=g3RRIfDiQra:eq:${staffData.username}&fields=trackedEntityInstance,orgUnit&paging=false`;
            
            console.log('🔍 Final check URL:', finalCheckUrl);
            
            const checkResponse = await fetch(finalCheckUrl, {
                method: 'GET',
                credentials: 'include',
                headers: getAuthHeader()
            });

            if (checkResponse.ok) {
                const checkData = await checkResponse.json();
                if (checkData.trackedEntityInstances && checkData.trackedEntityInstances.length > 0) {
                    console.log('✅ Found existing TEI after retries:', checkData.trackedEntityInstances[0].trackedEntityInstance);

                    const existingTei = checkData.trackedEntityInstances[0];
                    const isInCorrectOrgUnit = existingTei.orgUnit === orgUnitId;

                    return {
                        tei: { existingTei: true },
                        teiId: existingTei.trackedEntityInstance,
                        enrollmentId: null,
                        orgUnit: orgUnitId,
                        userRole: userRole,
                        existingTei: true,
                        reason: isInCorrectOrgUnit ? 'Found in final check, correct org' : 'Found in final check, wrong org'
                    };
                } else {
                    console.log('ℹ️ No TEI found in final check');
                }
            } else {
                console.warn('⚠️ Final check returned:', checkResponse.status);
                const errorText = await checkResponse.text();
                console.warn('⚠️ Error details:', errorText);
            }
        } catch (finalError) {
            console.error('❌ Final TEI check also failed:', finalError);
        }

        throw lastError || new Error('TEI creation failed after all attempts');

    } catch (error) {
        console.error('❌ TEI creation failed:', error);
        throw new Error(`TEI creation failed: ${error.message}`);
    }
}


// ✅ ADD THIS FUNCTION if not already present
const getParentOrgUnit = async (engine, orgUnitId) => {
  try {
    const response = await engine.query({
      orgUnit: {
        resource: 'organisationUnits',
        id: orgUnitId,
        params: {
          fields: 'id,name,parent[id,name]'
        }
      }
    });

    return response.orgUnit.parent;
  } catch (error) {
    console.error('Error fetching parent org unit:', error);
    return null;
  }
};


export async function updateStaff(userId, teiId, userData, auth) {
  try {
    console.log('🔄 updateStaff called with:', { userId, teiId, userData });

    if (!userId) {
      throw new Error('User ID is required');
    }

    // 1. Get current user data directly using the USER ID
    const userUrl = `${BASE_URL}/api/users/${userId}?fields=:all`;
    const userResponse = await fetch(userUrl, {
      headers: getAuthHeader(auth),
    });

    if (!userResponse.ok) {
      const errorText = await userResponse.text();
      console.error('❌ User fetch failed:', { status: userResponse.status, errorText });
      throw new Error(`Failed to fetch user: ${userResponse.status} - ${errorText}`);
    }

    const currentUser = await userResponse.json();
    console.log('📊 Current user data:', currentUser);

    // 2. Prepare update payload with correct structure
    const updatedUser = {
      // Preserve all existing properties
      ...currentUser,

      // Update only the fields we want to change
      firstName: userData.firstName || currentUser.firstName,
      surname: userData.surname || currentUser.surname,
      email: userData.email || currentUser.email,
      phoneNumber: userData.phoneNumber || currentUser.phoneNumber,
    };

    // ==================== CRITICAL: Handle organisation units (DEPARTMENT) ====================
    if (userData.organisationUnits && userData.organisationUnits.length > 0) {
      // Format organisation units correctly
      updatedUser.organisationUnits = userData.organisationUnits.map(ou => ({
        id: ou.id || ou
      }));

      // Also update dataViewOrganisationUnits for search visibility
      updatedUser.dataViewOrganisationUnits = userData.organisationUnits.map(ou => ({
        id: ou.id || ou
      }));
      console.log('🏢 Updated organisation units:', updatedUser.organisationUnits);
    } else {
      // PRESERVE existing organisation units
      updatedUser.organisationUnits = currentUser.organisationUnits;
      updatedUser.dataViewOrganisationUnits = currentUser.dataViewOrganisationUnits;
    }

    // ==================== CRITICAL: Handle user roles ====================
    if (userData.userRoles && userData.userRoles.length > 0) {
      // Update roles within userCredentials
      updatedUser.userCredentials = {
        ...currentUser.userCredentials,
        userRoles: userData.userRoles.map(roleId => ({ id: roleId }))
      };
      console.log('🎯 Updated user roles:', updatedUser.userCredentials.userRoles);
    } else if (userData.userRole) {
      // If userRole is provided as a single string, convert to array
      updatedUser.userCredentials = {
        ...currentUser.userCredentials,
        userRoles: [{ id: userData.userRole }]
      };
      console.log('🎯 Updated single user role:', updatedUser.userCredentials.userRoles);
    } else {
      // PRESERVE existing userCredentials if no roles are provided
      updatedUser.userCredentials = currentUser.userCredentials;
      console.log('ℹ️ Preserving existing user roles');
    }

    // ==================== CRITICAL: Ensure userCredentials has all required fields ====================
    if (updatedUser.userCredentials) {
      // Preserve username and other critical credentials
      updatedUser.userCredentials = {
        ...updatedUser.userCredentials,
        // Ensure username is preserved
        username: updatedUser.userCredentials.username || currentUser.username,
        // Preserve ID
        id: updatedUser.userCredentials.id || currentUser.userCredentials?.id || userId,
        // Don't include password in updates unless explicitly provided
        ...(userData.password ? { password: userData.password } : {})
      };
    }

    console.log('📤 Final user update payload:', JSON.stringify(updatedUser, null, 2));

    // 5. Send update to Backend
    const putResponse = await fetch(`${BASE_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth),
      },
      body: JSON.stringify(updatedUser),
    });

    console.log('📨 Update response status:', putResponse.status);

    const responseText = await putResponse.text();
    console.log('📄 Update response text:', responseText);

    if (!putResponse.ok) {
      console.error('❌ User update failed:', {
        status: putResponse.status,
        responseText: responseText
      });

      let errorMessage = `Update failed with status ${putResponse.status}`;
      try {
        const errorJson = JSON.parse(responseText);
        errorMessage = errorJson.message || errorJson.response?.message || responseText;
      } catch (e) {
        errorMessage = responseText || putResponse.statusText;
      }

      throw new Error(errorMessage);
    }

    // Handle success
    if (putResponse.status === 204) {
      console.log('✅ User updated successfully (204 No Content)');
      return {
        status: 'success',
        message: 'User updated successfully',
        rolesUpdated: !!(userData.userRoles || userData.userRole),
        orgUnitsUpdated: !!userData.organisationUnits
      };
    }

    try {
      const responseJson = responseText ? JSON.parse(responseText) : { status: 'success' };
      console.log('✅ User update successful:', responseJson);
      return {
        ...responseJson,
        rolesUpdated: !!(userData.userRoles || userData.userRole),
        orgUnitsUpdated: !!userData.organisationUnits
      };
    } catch (e) {
      console.log('✅ User update successful (non-JSON response)');
      return {
        status: 'success',
        message: 'Update succeeded',
        rolesUpdated: !!(userData.userRoles || userData.userRole),
        orgUnitsUpdated: !!userData.organisationUnits
      };
    }

  } catch (error) {
    console.error('❌ Update staff error:', {
      error: error.message,
      stack: error.stack,
      userId,
      teiId,
      userData
    });
    throw error;
  }
}

// Separate function to update organisation unit
async function updateUserOrganisationUnit(userId, orgUnitId, auth) {
  try {
    console.log(`🏢 Updating organisation unit to: ${orgUnitId}`);

    // First verify the org unit exists
    const orgUnitUrl = `${BASE_URL}/api/organisationUnits/${orgUnitId}?fields=id,name`;
    const orgUnitResponse = await fetch(orgUnitUrl, {
      headers: getAuthHeader(auth),
    });

    if (!orgUnitResponse.ok) {
      throw new Error(`Organisation unit ${orgUnitId} not found`);
    }

    // Update user with ONLY organisationUnits field
    const updatePayload = {
      id: userId,
      organisationUnits: [{ id: orgUnitId }]
    };

    const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth),
      },
      body: JSON.stringify(updatePayload),
    });

    if (response.ok) {
      console.log(`✅ Organisation unit updated to ${orgUnitId}`);
    } else {
      const errorText = await response.text();
      console.warn(`⚠️ Organisation unit update failed: ${errorText}`);
    }

  } catch (error) {
    console.error('❌ Organisation unit update error:', error.message);
    // Don't throw - this is non-critical
  }
}

// Separate function to update user roles
async function updateUserRoles(userId, userRoles, auth) {
  try {
    console.log(`🎯 Updating user roles: ${userRoles.join(', ')}`);

    // Update user with ONLY userCredentials field
    const updatePayload = {
      id: userId,
      userCredentials: {
        userRoles: userRoles.map(roleId => ({ id: roleId }))
      }
    };

    const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth),
      },
      body: JSON.stringify(updatePayload),
    });

    if (response.ok) {
      console.log(`✅ User roles updated`);
    } else {
      const errorText = await response.text();
      console.warn(`⚠️ User roles update failed: ${errorText}`);
    }

  } catch (error) {
    console.error('❌ User roles update error:', error.message);
    // Don't throw - this is non-critical
  }
}


async function getDataViewOrgUnits(userOrgUnitId, userRole, auth) {
  try {
    console.log(`🔍 Getting data view org units for ${userRole} in ${userOrgUnitId}`);

    // Get user org unit with path
    const userOrgUnit = await fetch(`${BASE_URL}/api/organisationUnits/${userOrgUnitId}?fields=id,name,path,level`, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    }).then(res => res.json());

    console.log('📍 User org unit:', userOrgUnit);

    const userPath = userOrgUnit.path;
    console.log('📍 User org unit path:', userPath);

    // Use role-specific visibility mapping
    const ROLE_VISIBILITY_MAP = {
      'nLSSLch316I': { higher: 6, lower: 7 }, // CEO
      'adeqQmxfCLF': { higher: 5, lower: 7 }, // HOD  
      'gtMJNK0QVL7': { higher: 6, lower: 8 }, // Top Staff
      'vdVjNaqxblC': { higher: 7, lower: null } // Senior Staff
    };

    const visibilityLevels = ROLE_VISIBILITY_MAP[userRole];

    if (!visibilityLevels) {
      console.warn(`⚠️ No visibility levels defined for role: ${userRole}, using expanded path`);
      return await getExpandedOrgUnitPath(userOrgUnitId, auth);
    }

    console.log(`🎯 Visibility for ${userRole}: higher=${visibilityLevels.higher}, lower=${visibilityLevels.lower}`);

    let dataViewOrgUnits = [userOrgUnit];

    // Get higher level org units (if defined)
    if (visibilityLevels.higher !== null && visibilityLevels.higher !== undefined) {
      try {
        const higherLevelOrgs = await getOrgUnitsAtLevel(visibilityLevels.higher, userPath, auth);
        console.log(`⬆️ Added ${higherLevelOrgs.length} higher level org units (level ${visibilityLevels.higher})`);
        dataViewOrgUnits = [...dataViewOrgUnits, ...higherLevelOrgs];
      } catch (higherError) {
        console.warn(`⚠️ Could not fetch higher level org units: ${higherError.message}`);
      }
    }

    // Get lower level org units (if defined)
    if (visibilityLevels.lower !== null && visibilityLevels.lower !== undefined) {
      try {
        const lowerLevelOrgs = await getOrgUnitsAtLevel(visibilityLevels.lower, userPath, auth);
        console.log(`⬇️ Added ${lowerLevelOrgs.length} lower level org units (level ${visibilityLevels.lower})`);
        dataViewOrgUnits = [...dataViewOrgUnits, ...lowerLevelOrgs];
      } catch (lowerError) {
        console.warn(`⚠️ Could not fetch lower level org units: ${lowerError.message}`);
      }
    }

    // Remove duplicates and ensure we have valid org units
    const uniqueOrgUnits = dataViewOrgUnits
      .filter(ou => ou && ou.id)
      .filter((ou, index, array) => array.findIndex(item => item.id === ou.id) === index);

    console.log('📊 Final data view org units:', uniqueOrgUnits.length, 'unique org units');
    console.log('📍 Org units:', uniqueOrgUnits);

    return uniqueOrgUnits;

  } catch (error) {
    console.error('❌ Error in getDataViewOrgUnits:', error);
    // Enhanced fallback: return expanded org unit path
    try {
      return await getExpandedOrgUnitPath(userOrgUnitId, auth);
    } catch (fallbackError) {
      console.warn('⚠️ Expanded fallback failed, using basic org unit:', fallbackError.message);
      return [{ id: userOrgUnitId }];
    }
  }
}

// Helper function to get org units at specific level in hierarchy
async function getOrgUnitsAtLevel(level, parentPath, auth) {
  try {
    console.log(`🔍 Fetching org units at level ${level} in path: ${parentPath}`);

    // Use proper API version and correct filter syntax
    const url = `${BASE_URL}/api/organisationUnits?fields=id,name,level&filter=level:eq:${level}&paging=false`;

    console.log('📡 API URL:', url);

    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    }

    const response = await res.json();
    console.log(`📊 Raw org units at level ${level}:`, response.organisationUnits);

    // Filter org units that are in the parent path
    const orgUnitsInPath = response.organisationUnits.filter(ou =>
      parentPath.includes(ou.id)
    );

    console.log(`📊 Filtered org units at level ${level} in path:`, orgUnitsInPath.length);
    return orgUnitsInPath;

  } catch (error) {
    console.error(`❌ Error fetching org units at level ${level}:`, error);
    // Return empty array instead of throwing to prevent blocking user creation
    return [];
  }
}

async function getExpandedDataViewOrgUnits(orgUnitId, auth) {
  try {
    console.log(`🔍 Getting expanded data view org units for: ${orgUnitId}`);

    // Get the org unit with full path information
    const orgUnitUrl = `${BASE_URL}/api/organisationUnits/${orgUnitId}?fields=id,path`;
    const orgUnitRes = await fetch(orgUnitUrl, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    });

    if (!orgUnitRes.ok) {
      throw new Error(`Failed to fetch org unit: ${orgUnitRes.status}`);
    }

    const orgUnitData = await orgUnitRes.json();
    const path = orgUnitData.path;

    // Extract all org unit IDs from the path (excluding empty strings)
    const pathOrgUnitIds = path.split('/').filter(id => id && id !== '');

    // Create array of org unit objects for dataViewOrganisationUnits
    const expandedOrgUnits = pathOrgUnitIds.map(id => ({ id }));

    console.log('📊 Expanded data view org units from path:', expandedOrgUnits);
    return expandedOrgUnits;

  } catch (error) {
    console.error('❌ Error getting expanded data view org units:', error);
    // Fallback: return just the original org unit
    return [{ id: orgUnitId }];
  }
}


// Supporting functions
async function createDHIS2User(userData, auth) {
  const url = `${BASE_URL}/api/users`;
  console.log('UserRole for role assignment for creating STAFF:', userData.userRole);
  if (!userData.userRole) {
    throw new Error('userRole is required for role assignment');
  }

  let dataViewOrgUnits = [];
  try {
    console.log('🔍 Determining data view org units...');
    dataViewOrgUnits = await getDataViewOrgUnits(userData.organisationUnits[0].id, userData.userRole, auth);
    console.log('✅ Data view org units determined:', dataViewOrgUnits);
  } catch (error) {
    console.warn('⚠️ Could not determine data view org units, using expanded fallback:', error.message);
    // Enhanced fallback: get the full org unit path
    try {
      dataViewOrgUnits = await getExpandedOrgUnitPath(userData.organisationUnits[0].id, auth);
    } catch (fallbackError) {
      console.warn('⚠️ Expanded fallback failed, using basic org unit:', fallbackError.message);
      dataViewOrgUnits = userData.organisationUnits;
    }
  }

  // ✅ ENSURE dataViewOrgUnits is properly formatted and includes required org units
  const formattedDataViewOrgUnits = dataViewOrgUnits.map(ou => {
    if (typeof ou === 'string') {
      return { id: ou };
    }
    return { id: ou.id };
  });

  // ✅ ALWAYS include the user's primary org unit and parent org units
  const finalDataViewOrgUnits = ensureMinimumDataViewAccess(
    formattedDataViewOrgUnits,
    userData.organisationUnits[0].id
  );

  console.log('📊 Final data view org units to assign:', finalDataViewOrgUnits);

  const payload = {
    firstName: userData.firstName,
    surname: userData.surname || userData.secondName,
    email: userData.email,
    phoneNumber: userData.phoneNumber,
    organisationUnits: userData.organisationUnits,
    dataViewOrganisationUnits: finalDataViewOrgUnits,
    userCredentials: {
      username: userData.userCredentials.username,
      password: userData.userCredentials.password || 'Default@123',
      userRoles: [{ id: userData.userRole }]
    }
  };

  console.log('✅ User creation payload with guaranteed data view access:', JSON.stringify(payload, null, 2));

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(auth)
    },
    body: JSON.stringify(payload),
  });

  const response = await parseDHIS2Response(res);
  console.log('User creation response:', response);

  // ✅ VERIFY data view org units were set correctly
  if (response?.response?.uid) {
    await verifyDataViewOrgUnits(response.response.uid, finalDataViewOrgUnits, auth);
  }

  return response;
}


export async function assignRoleToUser(userId, roleId, auth) {
  try {
    const url = `${BASE_URL}/api/users/${userId}`;

    const payload = {
      userCredentials: {
        userRole: [{ id: roleId }]
      }
    };

    const response = await fetch(url, {
      method: 'PUT', // Use PUT for updating users
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth),
      },
      body: JSON.stringify(payload),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || `HTTP ${response.status}`);
    }

    try {
      return text ? JSON.parse(text) : { status: 'success' };
    } catch (e) {
      return { status: 'success', rawResponse: text };
    }

  } catch (error) {
    console.error('Role assignment failed:', { userId, roleId, error: error.message });
    throw error;
  }
}

let callCount = 0;
// Check if a user exists by username
export const checkUserExistsByUsername = async (username, engine) => {
  const callId = ++callCount;
  console.log(`[${callId}] checkUserExistsByUsername called with:`, {
    username,
    engine,
    engineType: typeof engine,
    hasQuery: typeof engine?.query === 'function',
    stack: new Error().stack // This will show where it's called from
  });
  try {
    console.log('Checking user existence for:', username);
    console.log('Engine:', engine);

    // Fallback if engine is not provided
    if (!engine || typeof engine.query !== 'function') {
      console.warn('Engine not provided, falling back to fetch');
      return await checkUserExistsByUsernameFetch(username);
    }

    const { users } = await engine.query({
      users: {
        resource: 'users',
        params: {
          filter: `username:eq:${username}`,
          fields: 'id',
          paging: false
        }
      }
    });

    console.log('User check result:', users);
    console.log('User check type:', typeof users);
    console.log('User check isArray:', Array.isArray(users));

    // Handle different response structures
    let usersArray = null;
    if (Array.isArray(users)) {
      usersArray = users;
    } else if (users && typeof users === 'object' && Array.isArray(users.users)) {
      usersArray = users.users;
    } else if (users && typeof users === 'object' && users.length > 0) {
      usersArray = users;
    }

    const result = usersArray?.length > 0 ? usersArray[0] : null;
    console.log('User check return value:', result, 'truthiness:', !!result);
    return result;
  } catch (error) {
    console.error('Error checking user existence:', error);
    throw error;
  }
};


/**
 * Send custom invitation via Node.js backend
 * This completely bypasses DHIS2 email system
 */
export const sendCustomInvitation = async (engine, userData) => {
    console.log('📧 Sending custom invitation via Node.js backend...');
    console.log('📋 User data:', {
        firstName: userData.firstName,
        surname: userData.surname || userData.secondName,
        email: userData.email,
        username: userData.username,
        userRoles: userData.userRoles || (userData.userRole ? [userData.userRole] : []),
        orgUnit: userData.orgUnit
    });

    try {
        //  Get the API URL from environment
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' 
          ? '/backend-api'  // ✅ Relative path (uses same protocol as page)
          : 'http://localhost:3001');
        
        // ✅ Prepare the payload for the backend
        const payload = {
            firstName: userData.firstName,
            surname: userData.surname || userData.secondName || '',
            email: userData.email,
            username: userData.username,
            userRoles: userData.userRoles || (userData.userRole ? [userData.userRole] : []),
            organisationUnits: userData.organisationUnits || [{ id: userData.orgUnit }],
            dataViewOrganisationUnits: userData.dataViewOrganisationUnits || [{ id: userData.orgUnit }],
            redirectUrl: userData.frontendUrl || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'),
            sendEmail: true,
            expiryDays: 4
        };

        console.log('📤 Sending to backend:', `${API_BASE_URL}/api/users/invite`);
        console.log('📦 Payload:', payload);

        // ✅ Call the Node.js backend API
        const response = await fetch(`${API_BASE_URL}/api/users/invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        // ✅ Parse the response
        const responseData = await response.json();

        if (!response.ok) {
            console.error('❌ Backend error:', responseData);
            throw new Error(responseData.error || 'Failed to send invitation via backend');
        }

        console.log('✅ Custom invitation sent via Node.js backend:', responseData);

        // ✅ Return the result with the invitation URL
        return {
            success: true,
            userId: responseData.data?.userId,
            invitationToken: responseData.data?.invitationToken,
            invitationUrl: responseData.data?.invitationUrl,
            emailSent: responseData.data?.emailSent || false,
            message: responseData.message || 'Invitation sent successfully'
        };

    } catch (error) {
        console.error('❌ Backend invitation failed:', error.message);
        
        // ✅ Show a user-friendly error
        const errorMessage = error.message || 'Failed to send invitation. Please try again.';
        
        // ✅ Try the fallback only if backend is completely unavailable
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            console.warn('⚠️ Backend unreachable, trying fallback...');
            try {
                return await sendCustomInvitationFallback(engine, userData);
            } catch (fallbackError) {
                console.error('❌ Fallback also failed:', fallbackError.message);
                throw new Error('Could not send invitation. Please check if the backend server is running.');
            }
        }
        
        throw new Error(errorMessage);
    }
};

/**
 * Fallback: Direct DHIS2 invitation (only used when backend is down)
 */
const sendCustomInvitationFallback = async (engine, userData) => {
    console.log('📧 Using fallback: Direct DHIS2 invitation...');
    
    const userRoles = userData.userRoles || (userData.userRole ? [userData.userRole] : []);
    
    // Get current user's org unit
    let currentUserOrgUnit = null;
    try {
        const { me } = await engine.query({
            me: {
                resource: 'me',
                params: { fields: 'organisationUnits[id,name,level]' }
            }
        });
        currentUserOrgUnit = me.organisationUnits?.[0];
    } catch (e) {
        console.warn('Could not get current user org unit:', e);
    }

    // Prepare org unit arrays
    const organisationUnits = [];
    const dataViewOrganisationUnits = [];

    if (userData.orgUnit) {
        organisationUnits.push({ id: userData.orgUnit });
        dataViewOrganisationUnits.push({ id: userData.orgUnit });
    }

    if (currentUserOrgUnit && currentUserOrgUnit.id !== userData.orgUnit) {
        organisationUnits.push({ id: currentUserOrgUnit.id });
        dataViewOrganisationUnits.push({ id: currentUserOrgUnit.id });
    }

    // Create user via DHIS2
    const userPayload = {
        firstName: userData.firstName,
        surname: userData.surname || userData.secondName,
        email: userData.email,
        userCredentials: {
            username: userData.username,
            userRoles: userRoles.map(roleId => ({ id: roleId }))
        },
        organisationUnits: organisationUnits,
        dataViewOrganisationUnits: dataViewOrganisationUnits
    };

    try {
        const createResponse = await engine.mutate({
            resource: 'users',
            type: 'create',
            data: userPayload
        });

        const userId = createResponse.response?.uid || createResponse.uid;

        // Generate reset token
        const tokenResponse = await engine.mutate({
            resource: `users/${userId}/reset`,
            type: 'create',
            data: {}
        });

        let token = '';
        if (typeof tokenResponse === 'string') {
            token = tokenResponse;
        } else if (tokenResponse && typeof tokenResponse === 'object') {
            token = tokenResponse.token || tokenResponse.id || '';
        }

        const frontendUrl = userData.frontendUrl || window.location.origin;
        const customUrl = `${frontendUrl}/set-password?token=${encodeURIComponent(token)}&username=${userData.username}`;

        return {
            success: true,
            userId,
            invitationToken: token,
            invitationUrl: customUrl,
            emailSent: false,
            message: 'User created via fallback. Please copy the invitation URL.'
        };

    } catch (error) {
        console.error('❌ Fallback failed:', error);
        throw error;
    }
};


// Fallback fetch version
const checkUserExistsByUsernameFetch = async (username) => {
  const response = await fetch(
    `${BASE_URL}/api/users.json?filter=username:eq:${encodeURIComponent(username)}&fields=id`,
    {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        ...getAuthHeader(), // You'll need to handle auth
      },
      credentials: 'include',
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data.users?.length > 0 ? data.users[0] : null;
  }
  throw new Error(`Failed to check user existence: ${response.status}`);
};

// And similarly for checkUserExistsByEmail:
export const checkUserExistsByEmail = async (email, auth) => {
  if (!email) {
    throw new Error('Email is required to check user existence');
  }

  const response = await fetch(
    `${BASE_URL}/api/users.json?filter=email:eq:${encodeURIComponent(email)}&fields=id`,
    {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        ...getAuthHeader(auth),
      },
      credentials: 'include',
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch users by email (${response.status})`);
  }
  const data = await response.json();
  return data.users?.length > 0 ? data.users[0] : null;
};

export async function addUserToUserGroup(userId, userGroupId, auth) {
  try {
    console.log(`🔗 Adding user ${userId} to user group ${userGroupId}`);

    // Verify initial state
    await verifyUserGroupState(userGroupId, auth, "BEFORE");

    // Approach 1: Get current user group with ALL required fields
    const userGroupUrl = `${BASE_URL}/api/userGroups/${userGroupId}?fields=id,name,displayName,users[id]`;
    console.log('📡 Fetching user group:', userGroupUrl);

    const userGroupRes = await fetch(userGroupUrl, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    });

    if (!userGroupRes.ok) {
      throw new Error(`Failed to fetch user group: ${userGroupRes.status}`);
    }

    const userGroupData = await userGroupRes.json();
    console.log('👥 Current user group data:', userGroupData);

    // Check if user is already in the group
    const existingUsers = userGroupData.users || [];
    const existingUserIds = existingUsers.map(user => user.id);

    if (existingUserIds.includes(userId)) {
      console.log('✅ User already in group');
      return { success: true, alreadyMember: true };
    }

    // Add new user to the list (preserve existing users)
    const updatedUsers = [
      ...existingUsers, // Keep all existing users
      { id: userId }    // Add new user
    ];

    // ✅ Include ALL required fields when updating user group
    const updatePayload = {
      id: userGroupId,
      name: userGroupData.name, // ✅ REQUIRED: Include the name
      displayName: userGroupData.displayName || userGroupData.name, // ✅ Include displayName
      users: updatedUsers
    };

    console.log('🔄 Update payload with required fields:', JSON.stringify(updatePayload, null, 2));
    console.log(`👥 Preserved ${existingUsers.length} existing users, adding 1 new user`);

    // Update user group
    const updateUrl = `${BASE_URL}/api/userGroups/${userGroupId}`;
    const updateRes = await fetch(updateUrl, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth)
      },
      body: JSON.stringify(updatePayload)
    });

    if (!updateRes.ok) {
      const errorText = await updateRes.text();
      throw new Error(`Failed to update user group: ${updateRes.status} - ${errorText}`);
    }

    const result = await updateRes.json();
    console.log('✅ User group assignment completed:', result);

    // Verify final state 
    await verifyUserGroupState(userGroupId, auth, "AFTER");

    return result;

  } catch (error) {
    console.error('❌ Error in addUserToUserGroup:', error);

    // Try alternative approach using the userGroups endpoint with users array
    try {
      console.log('🔄 Trying alternative approach...');
      const alternativeResult = await addUserToUserGroupAlternative(userId, userGroupId, auth);
      console.log('✅ Alternative approach succeeded:', alternativeResult);
      return alternativeResult;
    } catch (altError) {
      console.error('❌ Alternative approach also failed:', altError);

      // Don't throw error - just log and continue
      console.warn('⚠️ User group assignment failed, but continuing with user creation');
      return {
        success: false,
        error: error.message,
        requiresManualAssignment: true
      };
    }
  }
}

// Debug function to verify user group state
async function verifyUserGroupState(userGroupId, auth, operation = "unknown") {
  try {
    const verifyUrl = `${BASE_URL}/api/userGroups/${userGroupId}?fields=id,name,users[id,username]`;
    const verifyRes = await fetch(verifyUrl, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    });

    if (verifyRes.ok) {
      const groupData = await verifyRes.json();
      console.log(`🔍 VERIFY ${operation}: User group has ${groupData.users?.length || 0} users`);
      console.log(`🔍 VERIFY ${operation}: Users:`, groupData.users?.map(u => u.username).join(', '));
      return groupData;
    }
  } catch (error) {
    console.error(`❌ Verification failed for ${operation}:`, error);
  }
  return null;
}

// ✅ SAFE Alternative Approach - Use the specific members endpoint
async function addUserToUserGroupAlternative(userId, userGroupId, auth) {
  try {
    console.log('🔄 Using alternative approach: members endpoint');

    // Use the specific endpoint to add members without affecting existing ones
    const membersUrl = `${BASE_URL}/api/userGroups/${userGroupId}/members`;

    const membersPayload = {
      users: [{ id: userId }]
    };

    console.log('📡 Adding member via:', membersUrl);
    console.log('📦 Members payload:', membersPayload);

    const membersRes = await fetch(membersUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(auth)
      },
      body: JSON.stringify(membersPayload)
    });

    if (!membersRes.ok) {
      const errorText = await membersRes.text();
      throw new Error(`Failed to add member: ${membersRes.status} - ${errorText}`);
    }

    const result = await membersRes.json();
    console.log('✅ Member added successfully via alternative approach');
    return result;

  } catch (error) {
    console.error('❌ Alternative approach failed:', error);
    throw error;
  }
}



export const fetchOrgUnitTree = async (engine, rootOrgUnitId) => {
  try {
    const query = {
      organisationUnits: {
        resource: `organisationUnits/${rootOrgUnitId}`,
        params: {
          // Load 8 levels deep to cover levels 5, 6, 7, and 8
          fields: 'id,name,level,children[id,name,level,children[id,name,level,children[id,name,level,children[id,name,level,children[id,name,level,children[id,name,level,children[id,name,level,children[id,name,level]]]]]]]]',
          paging: false,
        },
      },
    };

    const { organisationUnits } = await engine.query(query);
    console.log('Org unit tree fetched successfully with 8-level nesting');
    return organisationUnits;
  } catch (error) {
    console.error('Error fetching org unit tree with engine:', error);
    throw new Error(`Failed to fetch organisation unit tree: ${error.message}`);
  }
};

async function deleteUser(userId, auth) {
  try {
    const url = `${BASE_URL}/api/users/${userId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeader(auth)
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`User deletion failed: ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error(`Failed to delete user ${userId}:`, error);
    throw error;
  }
}

export async function parseDHIS2Response(response) {
  // First check for redirects
  if (response.redirected) {
    throw new Error('Authentication required - session may have expired');
  }

  const text = await response.text();

  // Handle HTML responses (login pages)
  if (text.startsWith('<!DOCTYPE') || text.includes('<html')) {
    throw new Error('Received HTML response - likely authentication issue');
  }

  try {
    const json = text ? JSON.parse(text) : {};

    if (!response.ok) {
      const errors = json.response?.errorReports ||
        json.response?.importSummaries?.[0]?.conflicts || [];
      throw new Error(errors.map(e => e.message).join('; ') || 'Unknown error');
    }

    return json;
  } catch (e) {
    throw new Error(`Failed to parse response: ${text.substring(0, 100)}...`);
  }
}

//  VERIFY METADATA FUNCTION
export const verifyMetadata = async (engine, orgUnitId) => {
  // Example: Check if trackedEntityType exists
  const { trackedEntityType } = await engine.query({
    trackedEntityType: {
      resource: 'trackedEntityTypes/OaAbpIs1cV7',
      params: { fields: 'id,displayName' }
    }
  })
  if (!trackedEntityType) {
    throw new Error('Required tracked entity type not found')
  }
  // Add more checks as needed
  return true
}

const PROGRAM_ID = 'yiT00XKrrHP';
const OBJECTIVES_STAGE_ID = 'tGNlh8qtPrq';
const TASKS_STAGE_ID = 'NYv3H6Y4A2G';

// Generic function to fetch tracker events for a given program stage
export async function fetchTrackerEvents(programStageId) {
  const url = `${BASE_URL}/api/tracker/events?program=${PROGRAM_ID}&programStage=${programStageId}&fields=event,orgUnit,eventDate,status,dataValues[dataElement,value]&pageSize=500`;
  const response = await fetch(url, {
    headers: getAuthHeader(),
    credentials: 'include',
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch tracker events: ${response.status} - ${text}`);
  }
  const data = await response.json();
  return data.events || [];
}

// Fetch all objectives events
export async function fetchObjectives() {
  return await fetchTrackerEvents(OBJECTIVES_STAGE_ID);
}

// Fetch all tasks events
export async function fetchTasks() {
  return await fetchTrackerEvents(TASKS_STAGE_ID);
}


// Helper to ensure minimum data view access
function ensureMinimumDataViewAccess(dataViewOrgUnits, primaryOrgUnitId) {
  const primaryOrgUnit = { id: primaryOrgUnitId };

  // Always include the primary org unit
  const orgUnitsSet = new Set();
  orgUnitsSet.add(primaryOrgUnitId);

  // Add all provided org units
  dataViewOrgUnits.forEach(ou => orgUnitsSet.add(ou.id));

  // Convert back to array of objects
  const result = Array.from(orgUnitsSet).map(id => ({ id }));

  console.log('🔒 Ensured minimum data view access:', result);
  return result;
}

// Get expanded org unit path (fallback method)
async function getExpandedOrgUnitPath(orgUnitId, auth) {
  try {
    console.log(`🔍 Getting expanded org unit path for: ${orgUnitId}`);

    const orgUnitUrl = `${BASE_URL}/api/organisationUnits/${orgUnitId}?fields=id,path`;
    const orgUnitRes = await fetch(orgUnitUrl, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    });

    if (!orgUnitRes.ok) {
      throw new Error(`Failed to fetch org unit: ${orgUnitRes.status}`);
    }

    const orgUnitData = await orgUnitRes.json();
    const path = orgUnitData.path;

    // Extract all org unit IDs from the path
    const pathOrgUnitIds = path.split('/').filter(id => id && id !== '');

    const expandedOrgUnits = pathOrgUnitIds.map(id => ({ id }));
    console.log('📊 Expanded org unit path:', expandedOrgUnits);
    return expandedOrgUnits;

  } catch (error) {
    console.error('❌ Error getting expanded org unit path:', error);
    // Fallback: return just the original org unit
    return [{ id: orgUnitId }];
  }
}

// Verify data view org units were set correctly
async function verifyDataViewOrgUnits(userId, expectedDataViewOrgUnits, auth) {
  try {
    console.log(`🔍 Verifying data view org units for user: ${userId}`);

    // Wait a moment for the user to be fully saved
    await new Promise(resolve => setTimeout(resolve, 1000));

    const userUrl = `${BASE_URL}/api/users/${userId}?fields=dataViewOrganisationUnits[id]`;
    const userRes = await fetch(userUrl, {
      method: 'GET',
      credentials: 'include',
      headers: getAuthHeader(auth)
    });

    if (userRes.ok) {
      const userData = await userRes.json();
      const actualDataViewOrgUnits = userData.dataViewOrganisationUnits || [];

      console.log('✅ Actual data view org units after creation:', actualDataViewOrgUnits);
      console.log('✅ Expected data view org units:', expectedDataViewOrgUnits);

      // Check if they match
      const actualIds = actualDataViewOrgUnits.map(ou => ou.id).sort();
      const expectedIds = expectedDataViewOrgUnits.map(ou => ou.id).sort();

      const matches = JSON.stringify(actualIds) === JSON.stringify(expectedIds);

      if (!matches) {
        console.warn('⚠️ Data view org units mismatch! May require manual update.');
        console.warn('Expected:', expectedIds);
        console.warn('Actual:', actualIds);
      } else {
        console.log('🎉 Data view org units verified successfully!');
      }

      return matches;
    }
  } catch (error) {
    console.error('❌ Error verifying data view org units:', error);
  }
  return false;
}

// Add this function to force the search org units
const forceSearchOrgUnitsUpdate = async (engine, userId, dataViewOrgUnits) => {
  try {
    console.log('🔧 Force updating search org units via direct API...');

    // First, get the current user to preserve all data
    const userResponse = await engine.query({
      user: {
        resource: 'users',
        id: userId,
        params: {
          fields: '*'
        }
      }
    });

    const currentUser = userResponse.user;

    // Update with ALL user data plus our search org units
    const updatePayload = {
      ...currentUser,
      dataViewOrganisationUnits: dataViewOrgUnits
    };

    // Remove fields that can't be updated
    delete updatePayload.created;
    delete updatePayload.lastUpdated;
    delete updatePayload.href;

    const response = await engine.mutate({
      resource: 'users',
      id: userId,
      type: 'update',
      data: updatePayload
    });

    console.log('✅ Search org units force-updated successfully');
    return response;
  } catch (error) {
    console.error('❌ Failed to force update search org units:', error);
    throw error;
  }
};

